import { Grid, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

import type { DateFilterOption } from '@/components/DateFilters'
import DateFilters from '@/components/DateFilters'
import { getFilterDateRange } from '@/components/DateFilters/utils'
import DateTotalsGraph from '@/components/DateTotalsGraph'
import DownloadCSV from '@/components/DownloadCSV'
import TotalBox from '@/components/TotalBox'
import PaperedGridItem from '@/components/UI/PaperedGridItem'
import useUser from '@/hooks/useUser'
import {
  getCompletedActivitiesForUserQuery,
  getTotalCompletedActivitiesByDateForUserQuery,
} from '@/lib/apollo'
import { getTimeInterval } from '@/utils/dateRange'
import { genericErrorMessage } from '@/utils/error'
import { defaultGridSpacing } from '@/utils/themes'
import {
  addEmptyDatestoTotalActivitiesData,
  toDateFilterPayload,
} from '@/views/Executive/ActivityTab/utils'

import CompletionTable from './CompletedTable'
import { rowsPerPage } from './CompletedTable/utils'
import { getCompletedActivitiesForUserCsv, toDateTotalsGraphDataPoint } from './utils'

export type Props = {
  userId: string
  testId: string
  email: string
}

const CompletedTab: FC<Props> = ({ testId, userId, email }) => {
  const { customerId, token, accessKey } = useUser()

  const [selectedDateFilter, setSelectedDateFilter] = useState<DateFilterOption>('today')

  const [
    fetchTotalCompletedActivitesByDateForUser,
    {
      data: totalCompletedActivitiesData,
      loading: totalCompletedActivitiesLoading,
      error: totalCompletedActivitiesError,
    },
  ] = getTotalCompletedActivitiesByDateForUserQuery.useLazyQuery()

  const transformedTotalActivitiesData =
    getTotalCompletedActivitiesByDateForUserQuery.toTransformedData(
      totalCompletedActivitiesData,
    )

  const [
    fetchCompletedActivities,
    { data: completedActivitiesData, loading: activitiesLoading, error: activitiesError },
  ] = getCompletedActivitiesForUserQuery.useLazyQuery()

  const transformedCompletedActivitiesData =
    getCompletedActivitiesForUserQuery.toTransformedData(completedActivitiesData)

  const [page, setPage] = useState<number>(0)

  const timeInterval = getTimeInterval(selectedDateFilter)

  useEffect(() => {
    if (!customerId) {
      return
    }

    const dateFilters = toDateFilterPayload(getFilterDateRange(selectedDateFilter))

    fetchTotalCompletedActivitesByDateForUser({
      variables: {
        customerId,
        userId,
        timeInterval,
        filters: {
          includeNonCompletionActivities: false,
          includeCompletionActivities: true,
        },
        ...dateFilters,
      },
    })
  }, [
    fetchTotalCompletedActivitesByDateForUser,
    selectedDateFilter,
    timeInterval,
    customerId,
    userId,
  ])

  useEffect(() => {
    if (!customerId) {
      return
    }

    const dateFilters = toDateFilterPayload(getFilterDateRange(selectedDateFilter))

    fetchCompletedActivities({
      variables: {
        customerId,
        userId,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        filters: {
          includeNonCompletionActivities: false,
          includeCompletionActivities: true,
        },
        ...dateFilters,
      },
    })
  }, [fetchCompletedActivities, page, selectedDateFilter, customerId, userId])

  const totalActivityGraphData = addEmptyDatestoTotalActivitiesData({
    data: transformedTotalActivitiesData,
    timeInterval,
    ...getFilterDateRange(selectedDateFilter),
  })

  const selectedDates = getFilterDateRange(selectedDateFilter)

  return (
    <Grid container spacing={defaultGridSpacing} data-testid={testId}>
      <Grid item container xs={12} direction="column" alignItems="flex-end">
        <DateFilters
          selected={selectedDateFilter}
          onChangeSelected={setSelectedDateFilter}
        />
      </Grid>
      <PaperedGridItem xs={4}>
        <Typography component="h2" variant="h6" gutterBottom>
          Total completions
        </Typography>
        <TotalBox
          loading={activitiesLoading || transformedCompletedActivitiesData === undefined}
          errorMessage={activitiesError ? genericErrorMessage : undefined}
          total={transformedCompletedActivitiesData?.total}
        />
      </PaperedGridItem>
      <PaperedGridItem xs={8}>
        <Typography component="h2" variant="h6" gutterBottom>
          Total complete
        </Typography>
        <DateTotalsGraph
          data={totalActivityGraphData.map(toDateTotalsGraphDataPoint)}
          loading={
            totalCompletedActivitiesLoading ||
            transformedTotalActivitiesData === undefined
          }
          errorMessage={totalCompletedActivitiesError ? genericErrorMessage : undefined}
          timeInterval={timeInterval}
        />
      </PaperedGridItem>
      <PaperedGridItem xs={12}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography component="h2" variant="h6" gutterBottom>
            Browse completions
          </Typography>
          <DownloadCSV
            handleDownload={() =>
              getCompletedActivitiesForUserCsv({
                customerId,
                userId,
                email,
                startDate: selectedDates.startDate,
                endDate: selectedDates.endDate,
                token,
                accessKey,
              })
            }
          />
        </Stack>
        <CompletionTable
          errorMessage={activitiesError ? genericErrorMessage : undefined}
          loading={activitiesLoading}
          data={transformedCompletedActivitiesData}
          page={page}
          onChangePage={setPage}
        />
      </PaperedGridItem>
    </Grid>
  )
}

export default CompletedTab
